/** Spoken once when a voice dialog starts on an empty chat. */
export const VOICE_DIALOG_GREETING = "Hi, I'm Tower Assistant. What can I help you with?";

/** Spoken when a voice dialog starts on a chat that already has turns. */
export const VOICE_DIALOG_CONTINUE = "I'm listening. What else can I help you with?";

/** The opening line for a voice dialog: intro on an empty transcript, else continue. */
export function voiceDialogOpeningLine(hasTranscript: boolean): string {
  return hasTranscript ? VOICE_DIALOG_CONTINUE : VOICE_DIALOG_GREETING;
}

/** True when the chat already has a user or assistant turn. */
export function voiceDialogHasTranscript(entries: ReadonlyArray<{ role: string }>): boolean {
  return entries.some((entry) => entry.role === "user" || entry.role === "assistant");
}

/**
 * Spoken once when a voice dialog starts waiting on the assistant. A long
 * search continues with a quiet pulse instead of more lines.
 */
export const VOICE_HOLD_PHRASE = "Just a moment, I'm working on that.";

/**
 * Shortest spacing between two spoken hold lines. Within this window a fresh
 * thinking stretch keeps only the quiet pulse, so the line does not repeat
 * back-to-back across turns (or a mid-turn remount). Measured from the start of
 * the last spoken line.
 */
export const VOICE_HOLD_REPEAT_MS = 20_000;

/**
 * Module-level so the spacing survives a drawer or StrictMode remount in the
 * middle of a turn, matching how the audio cues keep their state module-global.
 * A sentinel of negative infinity lets the first line play immediately.
 */
let lastHoldSpokenAt = Number.NEGATIVE_INFINITY;

/** True when enough time has passed to speak the hold line again. */
export function shouldSpeakHoldPhrase(now: number = Date.now()): boolean {
  return now - lastHoldSpokenAt >= VOICE_HOLD_REPEAT_MS;
}

/** Record that the hold line just started, so the spacing window begins now. */
export function markHoldPhraseSpoken(now: number = Date.now()): void {
  lastHoldSpokenAt = now;
}

/** Clear the spacing so the next hold line is allowed at once (fresh dialog). */
export function resetHoldPhraseCooldown(): void {
  lastHoldSpokenAt = Number.NEGATIVE_INFINITY;
}

/** Asked after a spoken answer so the user knows the mic is theirs again. */
export const VOICE_DIALOG_FOLLOW_UP = "Is there anything else I can help you with?";

/**
 * The line read after an assistant reply. A reply that already asks something
 * is left as it is, so the dialog does not ask a second question.
 */
export function voiceDialogSpokenReply(text: string): string {
  if (text.includes("?")) {
    return text;
  }
  return `${text} ${VOICE_DIALOG_FOLLOW_UP}`;
}

/**
 * Whether the spoken reply asked a question, so a short "no" should close the
 * dialog rather than be sent on. True when the line contains "?" — the canned
 * {@link VOICE_DIALOG_FOLLOW_UP}, a model help offer, or any clarifying
 * question the assistant already asked out loud.
 */
export function voiceDialogOffersHelp(text: string): boolean {
  return text.includes("?");
}

/** Spoken after the user declines more help, just before the voice dialog turns off. */
export const VOICE_DIALOG_GOODBYE = "Goodbye. I'm here if you need me.";

/**
 * Spoken when the microphone cannot be used (permission denied or no device),
 * just before the voice dialog ends. The composer mic tooltip then explains the
 * same error so the user can recover.
 */
export const VOICE_DIALOG_MIC_BLOCKED =
  "I can't access your microphone. Please check the permission and try again.";

/**
 * Quiet stretch after the last recognized words before a voice dialog sends.
 * Short drafts wait longer (noise / fragments); longer thoughts send sooner.
 */
export const VOICE_UTTERANCE_GAP_MS = 1400;
export const VOICE_UTTERANCE_GAP_SHORT_MS = 1000;
export const VOICE_UTTERANCE_GAP_LONG_MS = 2000;

/** How long to wait after `draft` before sending, by word count. */
export function voiceUtteranceGapMs(draft: string): number {
  const words = draft
    .trim()
    .split(/\s+/)
    .filter((word) => word.length > 0);
  if (words.length <= 2) {
    return VOICE_UTTERANCE_GAP_LONG_MS;
  }
  if (words.length >= 8) {
    return VOICE_UTTERANCE_GAP_SHORT_MS;
  }
  return VOICE_UTTERANCE_GAP_MS;
}

export type VoiceLaunchAction =
  | "start"
  | "interrupt"
  | "send"
  | "exit"
  | "pause"
  | "resume"
  | "listen";

export interface VoiceLaunchCommand {
  id: number;
  action: VoiceLaunchAction;
  /** A second click arrived before the greeting could start. Listen immediately. */
  skipGreeting?: boolean;
}

/** What the audio-dialog buttons are showing the user to do. */
export type VoiceDialogPhase = "idle" | "listening" | "speaking" | "thinking";

/** What the launcher needs in order to draw the floating mic. */
export interface VoiceLaunchActivity {
  listening: boolean;
  greeting: boolean;
  /** FAB voice conversation is on until the close button ends it. */
  voiceMode: boolean;
  /** The user froze the dialog. Phase color stays where it stopped. */
  paused: boolean;
  /** Color the dialog control should use. Idle keeps the Munis-pill fill. */
  phase: VoiceDialogPhase;
  disabled: boolean;
  tooltip: string;
}

export const VOICE_MODE_START_LABEL = "Start voice conversation";
export const VOICE_MODE_STOP_TOOLTIP = "Stop voice conversation";
export const VOICE_PAUSE_LABEL = "Pause voice conversation";
export const VOICE_RESUME_LABEL = "Resume voice conversation";

export const VOICE_PHASE_LABEL: Record<VoiceDialogPhase, string> = {
  idle: VOICE_MODE_START_LABEL,
  listening: "Speak now",
  speaking: "Listen",
  thinking: "Assistant is thinking",
};

export const VOICE_HINT_START_LABEL = "Start voice dialog";
export const VOICE_HINT_PAUSED_LABEL = "Paused - tap to resume";

/** Words on the status pill. Pause keeps the phase color and changes the line. */
export function voicePhaseHintLabel(phase: VoiceDialogPhase, paused: boolean): string {
  if (paused) {
    return VOICE_HINT_PAUSED_LABEL;
  }
  if (phase === "idle") {
    return VOICE_HINT_START_LABEL;
  }
  return VOICE_PHASE_LABEL[phase];
}

/**
 * One phase for both audio-dialog buttons. Speaking wins over the mic, and the
 * mic wins over a reply that is still being generated, so the color tells the
 * user whether to talk or to listen.
 */
export function voiceDialogPhase(
  input: Readonly<{
    voiceMode: boolean;
    listening: boolean;
    greeting: boolean;
    speaking: boolean;
    streaming: boolean;
    /** Mic is open for pause/resume/end, not for the user's turn. */
    commandListen?: boolean;
  }>,
): VoiceDialogPhase {
  if (!input.voiceMode) {
    return "idle";
  }
  if (input.greeting || input.speaking) {
    return "speaking";
  }
  if (input.listening && !input.commandListen) {
    return "listening";
  }
  return "thinking";
}
